/**
 * 子平曆法／四柱（從 qimen-web-app 曆法模組複製改寫，執行期不耦合奇門）
 *
 * 來源：
 * - 日柱：Excel 1900 序號 MOD 公式（與奇門 Program Y9/Z9 一致）
 * - 年柱：立春界 + year_pillars.json（1864–2103）
 * - 月柱：節氣表 jieqi_1900_2100.json → 月支；月干 = MOD(年干序×2+月建序,10)
 * - 時柱：時辰表 hours.json；時干由日干推
 * - 23:00–23:59：日柱／年柱用次日 0:00（與奇門 DT16/W2 一致）
 */
import jiazi60 from '../data/jiazi60.json';
import yearPillars from '../data/year_pillars.json';
import jieqiDun from '../data/jieqi_dun.json';
import jieqiTable from '../data/jieqi_1900_2100.json';
import hoursData from '../data/hours.json';
import lunarTable from '../data/lunar_1900_2100.json';

export const TIANGAN = '甲乙丙丁戊己庚辛壬癸';
export const DIZHI = '子丑寅卯辰巳午未申酉戌亥';

export type Gender = '男' | '女';

export interface CalendarInput {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

export interface Pillar {
  gan: string;
  zhi: string;
  gz: string;
}

export interface JieqiInfo {
  name: string;
  zhi: string;
  /** ISO 牆鐘字串（UTC+8 語意，與預表一致） */
  time?: string;
}

export interface CalendarResult {
  input: CalendarInput;
  effective: {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    nextDayZi: boolean;
  };
  pillars: {
    year: Pillar;
    month: Pillar;
    day: Pillar;
    hour: Pillar;
  };
  jieqi: JieqiInfo;
  /** 農曆日（僅日數；來源同奇門 lunar 預表） */
  lunarDay: number | null;
  notes: string[];
}

type JiaziRow = { gz: string; gan: string; zhi: string; xunshou: string; idx: number };

const JIAZI = jiazi60 as JiaziRow[];
const YEAR_PILLARS = yearPillars as Record<string, string>;
const JIEQI_DUN = jieqiDun as { name: string; zhi: string; dun: string }[];
const JIEQI_BY_YEAR = jieqiTable as Record<string, { n: string; t: string }[]>;
const LUNAR = lunarTable as Record<string, number>;
const HOURS = hoursData as { start: number; end: number; zhi: string; nextDay: boolean }[];

/** 十二節（起運用；不含中氣） */
export const JIE_NAMES = [
  '小寒',
  '立春',
  '惊蛰',
  '清明',
  '立夏',
  '芒种',
  '小暑',
  '立秋',
  '白露',
  '寒露',
  '立冬',
  '大雪',
];

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/** Excel 1900 日期序號 */
export function excelSerial(y: number, m: number, d: number, h = 0, mi = 0): number {
  const epoch = Date.UTC(1899, 11, 30, 0, 0, 0);
  const t = Date.UTC(y, m - 1, d, h, mi, 0);
  return (t - epoch) / 86400000;
}

export function ganIndex(g: string): number {
  return TIANGAN.indexOf(g) + 1;
}

export function zhiIndex(z: string): number {
  return DIZHI.indexOf(z) + 1;
}

export function isYangStem(gan: string): boolean {
  return ganIndex(gan) % 2 === 1;
}

/** 月建序：寅=1 … 丑=12 */
export function monthOrdFromZhi(z: string): number {
  const map: Record<string, number> = {
    寅: 1,
    卯: 2,
    辰: 3,
    巳: 4,
    午: 5,
    未: 6,
    申: 7,
    酉: 8,
    戌: 9,
    亥: 10,
    子: 11,
    丑: 12,
  };
  return map[z] ?? 1;
}

export function dayPillarFromSerial(serial: number): Pillar {
  const s = Math.floor(serial);
  const gan = TIANGAN[mod(s - 2, 10)];
  const zhi = DIZHI[mod(s - 4, 12)];
  return { gan, zhi, gz: gan + zhi };
}

export function monthGan(yearGan: string, monthOrd: number): string {
  const ac4 = ganIndex(yearGan);
  let m = mod(ac4 * 2 + monthOrd, 10);
  if (m === 0) m = 10;
  return TIANGAN[m - 1];
}

export function hourGan(dayGan: string, hourZhi: string): string {
  const dg = ganIndex(dayGan);
  const hz = zhiIndex(hourZhi);
  let m = mod((dg - 1) * 2 + hz, 10);
  if (m === 0) m = 10;
  return TIANGAN[m - 1];
}

export function hourZhiInfo(hour: number, minute: number): { zhi: string; nextDay: boolean } {
  const t = hour + minute / 60;
  for (const h of HOURS) {
    if (h.start === 0 && h.end === 1) {
      if (t >= 0 && t < 1) return { zhi: h.zhi, nextDay: false };
    } else if (t >= h.start && t < h.end) {
      return { zhi: h.zhi, nextDay: h.nextDay };
    }
  }
  return { zhi: '子', nextDay: false };
}

function normalizeJieqiName(n: string): string {
  return n
    .replace(/穀/g, '谷')
    .replace(/驚/g, '惊')
    .replace(/蟄/g, '蛰')
    .replace(/處/g, '处')
    .replace(/滿/g, '满');
}

function isJieName(n: string): boolean {
  const s = normalizeJieqiName(n);
  return JIE_NAMES.some((j) => normalizeJieqiName(j) === s || s.startsWith(normalizeJieqiName(j).slice(0, 2)));
}

function findJieqi(y: number, m: number, d: number, h: number, mi: number): JieqiInfo {
  const prev = JIEQI_BY_YEAR[String(y - 1)] ?? [];
  const curr = JIEQI_BY_YEAR[String(y)] ?? [];
  const timeline: { name: string; t: number; zhi: string; time: string }[] = [];

  const metaOf = (nameHans: string): { zhi: string; name: string } => {
    const simplified = normalizeJieqiName(nameHans);
    const hit = JIEQI_DUN.find(
      (j) => normalizeJieqiName(j.name) === simplified || j.name === nameHans,
    );
    if (hit) return { zhi: hit.zhi, name: hit.name };
    return { zhi: '子', name: nameHans };
  };

  for (const jq of prev) {
    if (['大雪', '冬至'].includes(jq.n)) {
      const meta = metaOf(jq.n);
      timeline.push({ name: meta.name, t: Date.parse(jq.t), zhi: meta.zhi, time: jq.t });
    }
  }
  for (const jq of curr) {
    const meta = metaOf(jq.n);
    timeline.push({ name: meta.name, t: Date.parse(jq.t), zhi: meta.zhi, time: jq.t });
  }

  timeline.sort((a, b) => a.t - b.t);
  const localMs = new Date(y, m - 1, d, h, mi).getTime();

  let current = timeline[0] ?? { name: '冬至', t: 0, zhi: '子', time: '' };
  for (const item of timeline) {
    if (item.t <= localMs) current = item;
    else break;
  }
  return { name: current.name, zhi: current.zhi, time: current.time };
}

function getLichun(y: number): Date | null {
  const list = JIEQI_BY_YEAR[String(y)];
  if (!list) return null;
  const lc = list.find((j) => j.n === '立春');
  return lc ? new Date(lc.t) : null;
}

function yearPillarGz(y: number, dt: Date): string {
  const lichun = getLichun(y);
  let yy = y;
  if (lichun && dt < lichun) yy = y - 1;
  return (
    YEAR_PILLARS[String(yy)] ??
    (() => {
      const idx = mod(yy - 1864, 60);
      return JIAZI[idx].gz;
    })()
  );
}

function toPillar(gz: string): Pillar {
  return { gan: gz[0], zhi: gz[1], gz };
}

/** 建立四柱 */
export function computeCalendar(input: CalendarInput): CalendarResult {
  const notes: string[] = [];
  const { year, month, day, hour, minute } = input;
  const hz = hourZhiInfo(hour, minute);
  const nextDayZi = hz.nextDay;

  let ey = year,
    em = month,
    ed = day;
  if (nextDayZi) {
    const nd = new Date(year, month - 1, day + 1);
    ey = nd.getFullYear();
    em = nd.getMonth() + 1;
    ed = nd.getDate();
    notes.push('23:00–23:59 子時：日柱／年柱用次日 0:00');
  }

  const serial = excelSerial(ey, em, ed, nextDayZi ? 0 : hour, nextDayZi ? 0 : minute);
  const dayP = dayPillarFromSerial(serial);

  const dtForYear = nextDayZi
    ? new Date(ey, em - 1, ed, 0, 0)
    : new Date(year, month - 1, day, hour, minute);
  const yGz = yearPillarGz(nextDayZi ? ey : year, dtForYear);
  const yPillar = toPillar(yGz);

  const jq = findJieqi(year, month, day, hour, minute);
  const mOrd = monthOrdFromZhi(jq.zhi);
  const mGan = monthGan(yPillar.gan, mOrd);
  const mPillar: Pillar = { gan: mGan, zhi: jq.zhi, gz: mGan + jq.zhi };

  const hGan = hourGan(dayP.gan, hz.zhi);
  const hPillar: Pillar = { gan: hGan, zhi: hz.zhi, gz: hGan + hz.zhi };

  const lunarY = nextDayZi ? ey : year;
  const lunarM = nextDayZi ? em : month;
  const lunarD = nextDayZi ? ed : day;
  const dateKey = `${lunarY}-${String(lunarM).padStart(2, '0')}-${String(lunarD).padStart(2, '0')}`;
  const lunarDay = LUNAR[dateKey] ?? null;
  if (lunarDay == null) notes.push('農曆日超出預表 1900–2100');

  if (!JIEQI_BY_YEAR[String(year)]) {
    notes.push('節氣超出預表 1900–2100，結果可能不準');
  }

  return {
    input,
    effective: { year: ey, month: em, day: ed, hour, minute, nextDayZi },
    pillars: {
      year: yPillar,
      month: mPillar,
      day: dayP,
      hour: hPillar,
    },
    jieqi: jq,
    lunarDay,
    notes,
  };
}

export function jiaziIndex(gz: string): number {
  const i = JIAZI.findIndex((j) => j.gz === gz);
  return i >= 0 ? i : 0;
}

export function jiaziByIndex(idx: number): string {
  return JIAZI[mod(idx, 60)].gz;
}

export interface JieBoundary {
  name: string;
  time: Date;
  year: number;
}

/** 收集跨年附近所有「節」時刻（供大運起運） */
export function collectJieAround(y: number): JieBoundary[] {
  const out: JieBoundary[] = [];
  for (const yy of [y - 1, y, y + 1]) {
    const list = JIEQI_BY_YEAR[String(yy)] ?? [];
    for (const jq of list) {
      if (isJieName(jq.n)) {
        out.push({ name: jq.n, time: new Date(jq.t), year: yy });
      }
    }
  }
  out.sort((a, b) => a.time.getTime() - b.time.getTime());
  return out;
}

/** 出生時刻前後最近之節 */
export function nearestJie(
  birth: Date,
  forward: boolean,
): { jie: JieBoundary; days: number } | null {
  const y = birth.getFullYear();
  const list = collectJieAround(y);
  if (forward) {
    for (const j of list) {
      if (j.time.getTime() > birth.getTime()) {
        const days = (j.time.getTime() - birth.getTime()) / 86400000;
        return { jie: j, days };
      }
    }
  } else {
    for (let i = list.length - 1; i >= 0; i--) {
      const j = list[i];
      if (j.time.getTime() <= birth.getTime()) {
        const days = (birth.getTime() - j.time.getTime()) / 86400000;
        return { jie: j, days };
      }
    }
  }
  return null;
}

export { JIAZI, JIEQI_BY_YEAR };
